const wordSeparators = /[-_\\.+\s]+/g;
const notAlphaNumericOrSpace = /[^ a-zA-Z0-9]+/g;
const notAlphaNumericSpaceOrDash = /[^ a-zA-Z0-9-]/g;
const capitalizedFirstLetter = /[A-Z]+(?![a-z])|[A-Z]/g;

/**
 * Safely camelCases a string, taking into account acronyms, kebab-case, snake_case, and sentence casing as well as special characters
 * @param string
 * @returns A `string` in camelCase form
 */
export const camelCase = (string: string): string => {
  const cleanedString = string
    .replace(wordSeparators, ' ')
    .replace(notAlphaNumericOrSpace, '')
    .replace(capitalizedFirstLetter, ($, ofs) => (ofs ? ' ' : '') + $.trim().toLowerCase())
    .trim();
  const words = cleanedString.split(' ');
  const camelCasedWords = words.map((word, index) =>
    index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
  );
  const camelCasedString = camelCasedWords.join('');

  return camelCasedString;
};

/**
 * Safely kebab-cases a string, taking into account acronyms, camelCase, snake_case, and sentence casing, as well as special characters
 * @param string
 * @returns a kebab-cased string
 */
export const kebabCase = (str: string) =>
  str
    .trim()
    .replace(wordSeparators, '-')
    .replace(notAlphaNumericSpaceOrDash, '')
    .replace(capitalizedFirstLetter, ($, ofs) => (ofs ? '-' : '') + $.trim().toLowerCase())
    .replace(/--+/g, '-');

/**
 * Safely Title Cases a string, taking into account acronyms, kebab-case, snake_case, camelCase, and sentence casing as well as special characters
 * @param string
 * @returns A `string` in Title Case form
 */
export const titleCase = (string: string): string => {
  const cleanedString = string.replace(wordSeparators, ' ').replace(notAlphaNumericOrSpace, '').trim();
  const words = cleanedString.split(' ');
  const titleCasedWords = words.map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase());
  const titleCasedString = titleCasedWords.join(' ');

  return titleCasedString;
};

/**
 * Checks if the provided value is an array and if its length is greater than 0
 * @param value - The value to check
 * @returns A `boolean` indicating if the value is an array and if its length is greater than 0
 */
export const isNonEmptyArray = <T>(value: unknown): value is T[] => Array.isArray(value) && value.length > 0;

export const stringToId = (label: string | undefined): string => {
  if (!label) {
    return '';
  }

  return label
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9-]/g, '');
};

export const debounce = <T extends (...args: any[]) => void>(fn: T, delay = 300) => {
  let timer: ReturnType<typeof setTimeout>;

  return (...args: Parameters<T>) => {
    if (timer) {
      clearTimeout(timer);
    }

    timer = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

/**
 * Normalize phone input to keep only digits and enforce max 11 characters.
 * Prevents the user from inputting more than 11 digits.
 */
export const normalizePhoneInput = (value: string): string | null => {
  const digitsOnly = value.replace(/[^0-9]/g, '');

  if (digitsOnly.length <= 11) {
    return digitsOnly;
  }

  // Too many digits — return null to indicate "do not update"
  return null;
};

/**
 * Format a US/CA phone number into a readable string.
 *
 * @param digits Only the numeric digits (already cleaned)
 * @returns Formatted phone number string
 */
export const formatPhoneNumber = (digits: string): string => {
  if (digits.length === 11 && !digits.startsWith('1')) {
    // If 11 digits but doesn’t start with 1, treat it like 10 digits
    digits = digits.slice(0, 10);
  }

  switch (true) {
    case digits.length <= 3:
      return digits;
    case digits.length <= 6:
      return `(${digits.slice(0, 3)})-${digits.slice(3)}`;
    case digits.length <= 10:
      return `(${digits.slice(0, 3)})-${digits.slice(3, 6)}-${digits.slice(6)}`;
    case digits.length === 11 && digits.startsWith('1'):
      return `+1 (${digits.slice(1, 4)})-${digits.slice(4, 7)}-${digits.slice(7)}`;
    default:
      return digits;
  }
};

/**
 * Validates a 10-digit NPI number using the Luhn algorithm,
 * with an NPI-specific prefix of '80840' per CMS spec.
 *
 * @param npi - A 10-digit string to be validated as an NPI number
 * @returns true if the NPI is valid, false otherwise
 */
export const isValidNpi = (npi: string): boolean => {
  // Ensure the input is exactly 10 digits
  if (!/^\d{10}$/.test(npi)) {
    return false;
  }

  // NPI numbers use a fixed prefix '80840' before applying the Luhn algorithm
  // The check digit is calculated from: prefix + first 9 digits of the NPI
  const prefix = '80840';
  const base = prefix + npi.slice(0, 9);
  // Convert to array of digits and reverse it for Luhn processing (right-to-left)
  const digits = base.split('').map(Number);

  // Apply Luhn algorithm to the reversed digits
  const sum = digits.reduce((acc, digit, idx) => {
    let val = digit;

    // Double every second digit (starting from the right, so odd indexes here)
    if (idx % 2 === 1) {
      val *= 2;
      // If doubling results in two digits, subtract 9 (e.g., 12 becomes 3)
      if (val > 9) {
        val -= 9;
      }
    }

    return acc + val;
  }, 0);

  // The valid check digit makes the total sum a multiple of 10
  const computedCheckDigit = (10 - (sum % 10)) % 10;

  // The actual check digit is the last digit of the NPI
  const actualCheckDigit = Number(npi[9]);

  // Return true if the computed and actual check digits match
  return computedCheckDigit === actualCheckDigit;
};

/**
 * Replaces the first path segment of a URL slug.
 *
 * @param slug - The full URL path to transform (e.g. "/content-library/article/test").
 * @param replacement - The segment to substitute in at position one (e.g. "healthcare-insights").
 * @returns The transformed path with the first segment replaced (e.g. "/healthcare-insights/article/test").
 *
 * @example
 * replaceFirstPathSegment("/content-library/article/test", "healthcare-insights");
 * // => "/healthcare-insights/article/test"
 */
export const replaceFirstPathSegment = (slug: string | undefined, replacement: string): string | null => {
  if (!slug) {
    return null;
  }
  const parts = slug.split('/');
  parts[1] = replacement;

  return parts.join('/');
};
